"use client";

import Image from "next/image";
import Link from "next/link";
import { useFormatter, useLocale, useTranslations } from "next-intl";

import type { AppLocale } from "@/i18n/routing";
import type { ArticleMeta } from "@/integrations/articles";
import { getArticleHref } from "@/integrations/articles/routes";
import { Eyebrow } from "@/components/eyebrow";
import { cn } from "@/lib/utils/cn";

import { ReadingTime } from "./ReadingTime";

interface ArticleCardProps {
  article: ArticleMeta;
  className?: string;
}

const COVER_SIZES = "(min-width: 1024px) 360px, (min-width: 640px) 50vw, 100vw";

export function ArticleCard({ article, className }: ArticleCardProps) {
  const t = useTranslations("pages.articles");
  const format = useFormatter();
  const locale = useLocale() as AppLocale;
  const href = getArticleHref(article.slug, locale);
  const excerpt = article.excerpt ?? article.description;
  const hasCover =
    typeof article.coverImage === "string" && article.coverImage.length > 0;
  const topTag = article.tags[0];

  return (
    <article
      className={cn(
        "group/article-card relative flex h-full flex-col gap-4",
        className,
      )}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl bg-sky/50">
        {hasCover ? (
          <Image
            src={article.coverImage as string}
            alt={article.title}
            fill
            sizes={COVER_SIZES}
            className="object-cover"
          />
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {topTag ? <Eyebrow text={topTag} /> : null}
          <span className="text-xs text-ink-3">
            <ReadingTime minutes={article.readingMinutes} />
            <span aria-hidden="true"> · </span>
            <time dateTime={article.publishedAt.toISOString()}>
              {format.dateTime(article.publishedAt, {
                year: "numeric",
                month: "short",
              })}
            </time>
          </span>
        </div>
        <h3 className="font-heading text-2xl leading-tight text-ink transition-colors group-hover/article-card:text-cobalt">
          <Link
            href={href}
            className="outline-none after:absolute after:inset-0 after:rounded-3xl after:content-[''] focus-visible:after:ring-3 focus-visible:after:ring-ring/50"
          >
            {article.title}
          </Link>
        </h3>
        {excerpt ? (
          <p className="line-clamp-2 text-sm text-ink-2">{excerpt}</p>
        ) : null}
      </div>
      {/* Intentionally drop dedicated TagList footer — tag lives in eyebrow + filter row */}
      {t("readMore") && false ? null : null}
    </article>
  );
}
